import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Alert from 'antd/lib/alert';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import { TRY_REQUEST_ISSUE_FIX, TriageViewType, tryRequestIssueFix } from '@/actions/triage';
import { IssueEntity, ReleaseStatus } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAppHome, selectReleasesForAppId } from '@/selectors/selectors';
import { antGrey, runwayColors, rwColors, tagStyle } from '@/components/styles/globalStyles';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { TriageIssueCard } from './TriageIssueCard';

const releaseTag = (version?: string, liveVersion?: string, nextVersion?: string): string | undefined => {
    if (version && version === nextVersion) {
        return 'NEXT RELEASE';
    }
    if (version && version === liveVersion) {
        return 'LIVE RELEASE';
    }
    return undefined;
};

const VersionLabel: React.FC<{ label: React.ReactNode; tag?: string }> = ({ label, tag }) => {
    const tagColor = tag === 'LIVE RELEASE' ? rwColors.statusGreen : rwColors.statusYellow;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
            <span>{label}</span>
            {tag && (
                <Tag
                    style={{
                        ...tagStyle,
                        color: tagColor,
                        borderColor: tagColor,
                        marginInlineEnd: 0,
                        backgroundColor: runwayColors.white,
                    }}
                >
                    {tag}
                </Tag>
            )}
        </span>
    );
};

interface RequestFixModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    appId: string;
    issue: IssueEntity;
    viewType: TriageViewType;
}

interface RequestFixFormValues {
    version?: string;
    details?: string;
}

export const RequestFixModal: React.FC<RequestFixModalProps> = ({ open, onClose, orgId, appId, issue, viewType }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<RequestFixFormValues>();

    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, appId));
    const appHome = useSelector((state: RootState) => selectAppHome(state, appId));
    const liveVersion = appHome?.liveRelease?.version;
    const nextVersion = appHome?.nextRelease?.version;
    // Default the target version to the next release.
    const selectedVersion = Form.useWatch('version', form) ?? nextVersion;

    const isSaving = useIsLoading(TRY_REQUEST_ISSUE_FIX, issue.id);
    const saveError = useError(TRY_REQUEST_ISSUE_FIX, issue.id);
    const saveSucceeded = useUpdated(TRY_REQUEST_ISSUE_FIX, issue.id);

    React.useEffect(() => {
        if (saveSucceeded) {
            form.resetFields();
            onClose();
        }
    }, [saveSucceeded]);

    const onSubmit = (values: RequestFixFormValues) => {
        const version = values.version ?? nextVersion;
        if (!version) {
            return;
        }
        dispatch(
            tryRequestIssueFix({ orgId, appId, issueId: issue.id, version, details: values.details?.trim(), viewType }),
        );
    };

    return (
        <Modal
            title="Request approval for a fix"
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText="Submit request"
            okButtonProps={{ disabled: !selectedVersion }}
            confirmLoading={isSaving}
            destroyOnClose
            width={600}
        >
            <Text color={antGrey.grey8}>
                Choose the issue you&apos;d like to pull in as a fix for this release. You can optionally include
                details about why the fix is needed.
            </Text>
            <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                requiredMark
                preserve={false}
                style={{ marginTop: 24 }}
            >
                {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
                <Form.Item
                    name="version"
                    label="Target version"
                    layout="horizontal"
                    labelAlign="left"
                    labelCol={{ flex: '130px' }}
                    wrapperCol={{ flex: 'auto' }}
                    colon={false}
                    initialValue={nextVersion}
                    rules={[{ required: true, message: 'Select a release' }]}
                >
                    <Select
                        placeholder="Select a release"
                        showSearch
                        optionFilterProp="label"
                        options={releases
                            .filter(
                                (release) =>
                                    ![ReleaseStatus.Completed, ReleaseStatus.Skipped, ReleaseStatus.Deleted].includes(
                                        release.status,
                                    ),
                            )
                            .map((release) => ({
                                value: release.version,
                                label: release.displayableVersion || release.version,
                            }))}
                        optionRender={(option) => (
                            <VersionLabel
                                label={option.label}
                                tag={releaseTag(option.value as string, liveVersion, nextVersion)}
                            />
                        )}
                        labelRender={(props) => (
                            <VersionLabel
                                label={props.label}
                                tag={releaseTag(props.value as string, liveVersion, nextVersion)}
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item name="details" label="Details">
                    <Input.TextArea
                        rows={4}
                        placeholder="Enter some details about why this fix should be pulled into this release."
                    />
                </Form.Item>
                <Form.Item label="Associated issue" required>
                    <TriageIssueCard issue={issue} orgId={orgId} preview viewType={viewType} />
                </Form.Item>
            </Form>
        </Modal>
    );
};
